/**
 * The two platform facts the push enrolment flow has to branch on.
 *
 * There was no platform detection anywhere in the client before this, and it is added
 * reluctantly: user-agent sniffing ages badly. It is here because one platform makes
 * push conditional on something the user must do outside the app — see `isIosDevice`.
 *
 * Both take their inputs as arguments so the interesting cases can be reached from a
 * test. An iPhone in Safari and the same iPhone opened from the Home Screen differ in
 * nothing a jsdom environment provides on its own.
 */

/**
 * iOS, iPadOS included.
 *
 * Only ever used to *explain* a missing capability, never to decide one. Safari on iOS
 * does not expose `PushManager` until the app is installed to the Home Screen, so the
 * capability check leads and this answers "why" — which means the day Apple changes
 * that, the flow starts working without a code change.
 */
export function isIosDevice(
  userAgent: string = navigator.userAgent,
  maxTouchPoints: number = navigator.maxTouchPoints ?? 0,
): boolean {
  if (/iPhone|iPad|iPod/.test(userAgent)) return true;

  // iPadOS 13+ sends the desktop Safari agent verbatim. Touch points are the only thing
  // separating it from a Mac, and the distinction matters: an iPad has the same
  // install-before-alerts restriction an iPhone does.
  return /Macintosh/.test(userAgent) && maxTouchPoints > 1;
}

/** Running from the Home Screen icon rather than inside a browser tab. */
export function isStandalone(): boolean {
  if (typeof window === 'undefined') return false;

  // iOS never implemented `display-mode`; it has its own non-standard flag, and that
  // flag is the only signal available on the one platform where this is load-bearing.
  const iosStandalone = (navigator as { standalone?: boolean }).standalone === true;

  // Optional call: jsdom has no `matchMedia`, and neither do some older Android
  // WebViews this app is expected to survive.
  return iosStandalone || window.matchMedia?.('(display-mode: standalone)').matches === true;
}

/**
 * Android, for the one place the platform decides the words: the blocked-alerts
 * instructions, which name the OS settings path. Same shape as `isIosDevice`.
 */
export function isAndroidDevice(userAgent: string = navigator.userAgent): boolean {
  return /Android/.test(userAgent);
}
